/* eslint-disable max-len */
import { ui } from '@owenscorning/pcb.alpha';
import _ from 'lodash';

import Product from './Product';
import { wrapRef } from '../../../../../data';

const getTableCell = (text, isBold) => ({
  content: {
    ops: [
      {
        attributes: { bold: isBold },
        insert: text,
      },
    ],
  },
});

const getTableRow = (attribute, value) => [getTableCell(attribute, true), getTableCell(value, false)];

const getTableData = () => [
  {
    rows: [
      getTableRow('Warranty', ''),
      getTableRow('Wind Resistance', ''),
      getTableRow('Algae Resistance', ''),
      getTableRow('Nominal Size', ''),
      getTableRow('Exposure', ''),
      getTableRow('Shingles Per Square', ''),
      getTableRow('Bundles Per Square', ''),
      getTableRow('Coverage Per Square', ''),
    ],
    type: 'Body',
  },
];

const disclaimerBody = {
  ops: [
    {
      insert: `We strive to accurately reproduce all photographs of shingles in literature and for onscreen viewing. The roofing product colors you see are as accurate as technology allows. We suggest that you view a roofing display or several shingles to get a better idea of the actual color. To accurately judge your shingle and color choice, we recommend that you view it on an actual roof with a pitch similar to your own roof prior to making your final color selection.
      ‡ This image depicts Triple Layer Protection® and the amount of Triple Layer Protection may vary on a shingle-to-shingle basis. SureNail® Technology is proprietary with U.S. and foreign protection including U.S. Patent
      Nos. 6,471,812; 7,836,654; 8,156,704; 8,181,413; 8,240,102; 8,430,983; 8,607,521; 8,623,164; 8,752,351; 8,991,130; 9,121,178; and other patents pending.
      *See actual warranty for complete details, limitations, and requirements.
      ** Excludes non-Owens Corning® products such as flashing, fasteners, and wood decking`,
      attributes: {
        disclaimer: true,
      },
    },
  ],
};

const sectionContainsModule = (section, moduleType) => section?.modules?.some(module => module?.type === moduleType);

export const moveFindAContractorSectionAfterColorAndImagery = contents => {
  if (!Array.isArray(contents?.sections)) return contents;

  const colorAndImageryIndex = contents.sections.findIndex(section =>
    sectionContainsModule(section, 'ShingleColorAndImage')
  );
  const findAContractorIndex = contents.sections.findIndex(section =>
    sectionContainsModule(section, 'FindAContractorMini')
  );

  if (
    colorAndImageryIndex < 0 ||
    findAContractorIndex < 0 ||
    findAContractorIndex === colorAndImageryIndex ||
    findAContractorIndex === colorAndImageryIndex + 1
  ) {
    return contents;
  }

  const sections = [...contents.sections];
  const [findAContractorSection] = sections.splice(findAContractorIndex, 1);
  const adjustedColorAndImageryIndex =
    findAContractorIndex < colorAndImageryIndex ? colorAndImageryIndex - 1 : colorAndImageryIndex;

  sections.splice(adjustedColorAndImageryIndex + 1, 0, findAContractorSection);

  return { ...contents, sections };
};

const Shingle = Product(
  'www.owenscorning.com',
  'Product::Shingle',
  ui`AttributeSet`({
    query: {
      name: 'Shingle PDP Attributes',
      path: '/roofing',
    },
  }),
  [],
  null,
  {
    version: '0.1',
    displayName: 'Shingle PDP',
    paths: ['roofing/shingles'],
    settings: {
      postalRequired: ui`Switch`({
        label: 'Require Postal Code?',
        default: true,
        disabled: true,
        tip: 'Must be enabled for this content type.',
      }),
    },
  },
  [
    {
      title: 'Color & Imagery',
      unmovables: [true, true],
      initial: [
        {
          type: 'ShingleColorAndImage',
        },
        {
          type: 'StyleBoardsForColor',
        },
      ],
      modules: ['BasicContent', 'ContentAndImage', 'Reusable'],
    },
    {
      title: 'Find a Contractor',
      unmovables: [true],
      showHeading: true,
      initial: [
        {
          type: 'FindAContractorMini',
          data: {
            heading: '',
          },
        },
      ],
      modules: ['BasicContent', 'CardSet', 'Reusable'],
    },
    {
      title: 'Features & Benefits',
      showHeading: true,
      unmovables: [true],
      initial: [
        {
          type: 'ShingleFeaturesAndBenefits',
        },
      ],
      modules: ['BasicContent', 'ContentAndImage', 'CardSet', 'Reusable'],
    },
    {
      title: 'Technical Information',
      showHeading: true,
      unmovables: [true, true],
      initial: [
        {
          type: 'TechnicalCharacteristicsTable',
        },
        {
          type: 'StandardsAndDocuments',
          data: {
            containerSize: { selection: 'm' },
          },
        },
      ],
      modules: ['Table', 'BasicContent', 'ContentAndImage', 'Accordion', 'Reusable'],
    },
    {
      title: 'Design Inspiration for Your Home',
      showHeading: true,
      unmovables: [true],
      initial: [
        {
          type: 'CardSet',
          data: {
            carousel: {
              enabled: true,
            },
          },
        },
      ],
      modules: ['BasicContent', 'CardSet', 'ContentAndImage', 'Reusable'],
    },
    {
      title: 'TPRS',
      unmovables: [true],
      initial: [
        {
          type: 'Reusable',
          data: {
            reference: wrapRef('Cms::Content', {
              type: 'Reusable',
              path: '',
              name: 'TPRS PDP Section',
            }),
          },
        },
      ],
      modules: ['BasicContent', 'ContentAndImage', 'CardSet', 'Reusable'],
    },
    {
      title: 'Ratings & Reviews',
      showHeading: true,
      unmovables: [true],
      initial: ['BVRatings'],
      modules: ['BasicContent', 'Reusable'],
    },
    {
      title: 'Disclaimers',
      unmovables: [true],
      initial: [
        {
          type: 'BasicContent',
          data: {
            content: {
              heading: '',
              prehead: '',
              bodyCopy: disclaimerBody,
            },
          },
        },
      ],
      modules: [],
    },
  ],
  '/roofing/shingles',
  false,
  'roofing'
);

const Roofing = _.memoize(UI => {
  const definition = Shingle(UI);
  const readProduct = definition.read;

  return {
    ...definition,
    read: input => {
      const result = readProduct(input);
      return {
        ...result,
        contents: moveFindAContractorSectionAfterColorAndImagery(result.contents),
      };
    },
  };
});

export default Roofing;
